// DynamicMetabaseDashboard.jsx - Dynamic Metabase dashboard for any clinic

import React, { useEffect, useState } from 'react';
import { 
  Box, 
  CircularProgress, 
  Alert, 
  Button, 
  Typography,
  Paper 
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';

const METABASE_URL = process.env.REACT_APP_METABASE_URL || 'http://localhost:3001';

const DynamicMetabaseDashboard = ({ 
  dashboardType = 'executive',
  clinicSchemaName, // e.g., "ksl_clinic_5", "clinic_pabau_demo", etc.
  clinicName, // Display name
  height = '700px'
}) => {
  const [loading, setLoading] = useState(true);
  const [dashboardUrl, setDashboardUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // LOCAL DEVELOPMENT ONLY - Tenant-specific dashboard public UUIDs
    // Public dashboards bypass CSP restrictions
    // In production, use signed embedding with JWT tokens for security
    const TENANT_DASHBOARDS = {
      'ksl_clinic_5': {
        // Public UUIDs for KSL dashboards in KSL Clinic Analytics collection
        executive: 'c169566b-ec6e-4c83-b86a-17571fdeb97a',      // KSL Clinic - Executive Overview (ID: 7)
        clients: '54d606a1-1123-43e2-a327-6b20da5fa414',        // KSL Clinic - Client Analytics (ID: 8)
        appointments: '011d7c83-266b-4be3-8d9d-5a1ffe2bdaaa',  // KSL Clinic - Appointment Analytics (ID: 9)
        services: 'a12953bb-fb24-4de9-94bc-0cf7268ba142',      // KSL Clinic - Service Performance (ID: 10)
        financial: '57b0a86e-7f02-481e-a9de-e426c4eaec2f'       // KSL Clinic - Financial Insights (ID: 11)
      },
      // Add more tenants as they're provisioned
      'clinic_pabau_demo': {
        executive: null, // Not provisioned yet
        clients: null,
        appointments: null,
        services: null,
        financial: null
      }
    };

    // Get the dashboard UUIDs for this tenant
    const tenantDashboards = TENANT_DASHBOARDS[clinicSchemaName];
    
    if (!tenantDashboards || !tenantDashboards[dashboardType]) {
      setError(`No dashboard configured for ${clinicName}. Please run tenant provisioning.`);
      setLoading(false);
      return;
    }

    const dashboardUuid = tenantDashboards[dashboardType];
    
    // Use public dashboard URL (no authentication required, no CSP issues)
    // NOTE: For production, implement signed embedding for security
    const url = `${METABASE_URL}/public/dashboard/${dashboardUuid}`;
    
    setDashboardUrl(url);
    setError(null);
    setLoading(false);
  }, [dashboardType, clinicSchemaName, clinicName]);

  const handleOpenInNewTab = () => {
    window.open(dashboardUrl, '_blank');
  };

  const handleRefresh = () => {
    setLoading(true);
    // Force iframe reload
    const iframe = document.getElementById('metabase-iframe');
    if (iframe) {
      iframe.src = iframe.src;
    }
    setTimeout(() => setLoading(false), 1000);
  };

  if (!clinicSchemaName || clinicSchemaName === 'null' || clinicSchemaName === 'undefined') {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        <Typography variant="h6" gutterBottom>No clinic selected</Typography>
        <Typography variant="body2">
          Please ensure you're logged in with a clinic account to view analytics dashboards.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header with clinic info */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">
            {clinicName || clinicSchemaName} Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Live data from schema: {clinicSchemaName}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenInNewTab}
          >
            Open in Metabase
          </Button>
        </Box>
      </Box>

      {/* Dashboard iframe */}
      <Paper elevation={2} sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
        {loading && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              bgcolor: 'background.paper',
              zIndex: 10
            }}
          >
            <CircularProgress />
          </Box>
        )}
        
        {dashboardUrl && (
          <iframe
            id="metabase-iframe"
            src={dashboardUrl}
            width="100%"
            height={height}
            frameBorder="0"
            title={`${clinicName} ${dashboardType} Dashboard`}
            style={{
              border: 'none',
              display: loading ? 'none' : 'block'
            }}
            onLoad={() => setLoading(false)}
            onError={() => {
              setError('Failed to load dashboard. Please ensure Metabase is running and you have access.');
              setLoading(false);
            }}
          />
        )}
        
        {error && (
          <Alert severity="info" sx={{ m: 2 }}>
            {error}
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                <strong>Local Development Note:</strong> This is configured for local development only.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                To provision dashboards for your clinic, run:
              </Typography>
              <Typography variant="body2" component="code" sx={{ 
                mt: 1, 
                p: 1, 
                bgcolor: 'grey.100', 
                display: 'block',
                fontFamily: 'monospace',
                fontSize: '12px'
              }}>
                python scripts/metabase_tenant_provisioning.py
              </Typography>
            </Box>
          </Alert>
        )}
      </Paper>

      {/* Info box */}
      {dashboardUrl && (
        <Alert severity="info" sx={{ mt: 2 }} icon={false}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Dashboard: {dashboardType.charAt(0).toUpperCase() + dashboardType.slice(1)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <strong>Local Development:</strong> Showing {clinicName} data from {clinicSchemaName} schema.
            {' '}Each clinic sees only their own data. Production will use secure JWT-based embedding.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default DynamicMetabaseDashboard;